import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Loader2, UserCog } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const empty = { email: "", password: "", name: "", designation: "", role: "staff" };

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, designation: u.designation, role: u.role });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, designation: form.designation, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { disabled: !u.disabled });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Access Control</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">User Management</h1>
        </div>
        <Button data-testid="new-user-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New User
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Email</th>
              <th className="text-left p-3 font-semibold">Designation</th>
              <th className="text-left p-3 font-semibold">Role</th>
              <th className="text-left p-3 font-semibold">Active</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold">{u.name}</td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3 text-slate-600">{u.designation || "—"}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${
                    u.role === "admin" ? "bg-amber-500 text-slate-900" : "bg-slate-200 text-slate-700"
                  }`}>{u.role}</span>
                </td>
                <td className="p-3">
                  <Switch checked={!u.disabled} onCheckedChange={() => toggleDisabled(u)} disabled={u.id === me?.id} />
                </td>
                <td className="p-3 text-right space-x-1">
                  <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-2 hover:bg-slate-100"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} disabled={u.id === me?.id} className="p-2 hover:bg-red-50 text-red-600 disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No users.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit User" : "New User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Email *</Label>
              <Input data-testid="user-form-email" type="email" required disabled={!!editing} value={form.email} onChange={setField("email")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
              <Input data-testid="user-form-name" required value={form.name} onChange={setField("name")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Designation</Label>
              <Input value={form.designation} onChange={setField("designation")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">
                {editing ? "New Password (leave blank to keep current)" : "Password *"}
              </Label>
              <Input data-testid="user-form-password" type="password" required={!editing} value={form.password} onChange={setField("password")} className="rounded-none" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button data-testid="user-form-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
